import { autoinject, bindingMode, observable } from 'aurelia-framework';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { AuthService } from 'app/um/services/auth-service';
import { LookupService } from 'app/cd/services/lookup-service';
import { Toaster } from 'app/shared/lib/toaster';
import { apiCall } from 'app/shared/lib/essette-api';
import { getReference } from 'app/shared/models/essette-types';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { I18N } from 'aurelia-i18n';
import * as UM from 'app/um/models/essette-api';
import * as CD from 'app/cd/models/essette-api';

@autoinject()
export class SavePortalQueue extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.objectNull({ defaultBindingMode: bindingMode.toView })
    protected portalQueue: UM.PortalQueue = {};

    protected selectedLinesOfBusiness: CD.LineOfBusiness[] = [];
    protected selectedHealthPlans: CD.HealthPlan[] = [];
    protected selectedAuthSubClasses: UM.AuthSubClass[] = [];

    protected linesOfBusiness: ReadonlyArray<CD.LineOfBusiness> = [];
    protected healthPlans: ReadonlyArray<CD.HealthPlan> = [];
    protected authSubClasses: ReadonlyArray<UM.AuthSubClass> = [];

    @observable
    protected allowAnyLineOfBusiness = false;

    @observable
    protected allowAnyHealthPlan = false;

    @observable
    protected allowAnyAuthSubClass = false;

    ////****************** Element References ***************************************////

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly authService: AuthService,
        private readonly lookupService: LookupService,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    protected attached(): void {
        // load lookups, do not wait for results
        this.reload();
    }

    protected portalQueueChanged() {
        this.selectedLinesOfBusiness = this.portalQueue?.linesOfBusiness?.filter(lob => lob.isActive) ?? [];
        this.selectedHealthPlans = this.portalQueue?.healthPlans?.filter(hp => hp.isActive) ?? [];
        this.selectedAuthSubClasses = this.portalQueue?.authSubClasses?.filter(sc => sc.isActive) ?? [];
        this.allowAnyLineOfBusiness = this.portalQueue?.flag?.allowAnyLineOfBusiness || false;
        this.allowAnyHealthPlan = this.portalQueue?.flag?.allowAnyHealthPlan || false;
        this.allowAnyAuthSubClass = this.portalQueue?.flag?.allowAnyAuthSubClass || false;
    }

    ////****************** Events ***************************************************////
    protected async reload(): Promise<void> {
        await this.loadLookups();
    }

    protected allowAnyLineOfBusinessChanged(): void {
        this.selectedLinesOfBusiness = this.allowAnyLineOfBusiness ? [] : this.portalQueue?.linesOfBusiness?.filter(lob => lob.isActive) || [];
    }

    protected allowAnyHealthPlanChanged(): void {
        this.selectedHealthPlans = this.allowAnyHealthPlan ? [] : this.portalQueue?.healthPlans?.filter(hp => hp.isActive) || [];
    }

    protected allowAnyAuthSubClassChanged(): void {
        this.selectedAuthSubClasses = this.allowAnyAuthSubClass ? [] : this.portalQueue?.authSubClasses?.filter(sc => sc.isActive) || [];
    }

    ////****************** Functions ************************************************////
    protected constructSaveObject(): UM.PortalQueue {
        if(!this.portalQueue) {
            throw new Error('Bindable property "portalQueue" must be bound');
        }

        const savePortalQueue: UM.PortalQueue = {
            id: this.portalQueue.id,
            description: this.portalQueue.description,
            code: this.portalQueue.code,
            flag: {
                isFallbackQueue: this.portalQueue.flag?.isFallbackQueue || false,
                allowAnyLineOfBusiness: this.allowAnyLineOfBusiness,
                allowAnyHealthPlan: this.allowAnyHealthPlan,
                allowAnyAuthSubClass: this.allowAnyAuthSubClass,
            },
            linesOfBusiness: [],
            healthPlans: [],
            authSubClasses: []
        }

        this.selectedLinesOfBusiness.forEach(lob => {
            const lobReference = getReference(lob);
            if(lobReference) savePortalQueue.linesOfBusiness?.push(lobReference);
        });

        this.selectedHealthPlans.forEach(hp => {
            const hpReference = getReference(hp);
            if(hpReference) savePortalQueue.healthPlans?.push(hpReference);
        });

        this.selectedAuthSubClasses.forEach(sc => {
            const scReference = getReference(sc);
            if(scReference) savePortalQueue.authSubClasses?.push(scReference);
        });

        return savePortalQueue;
    }

    public reset(): void {
        this.portalQueue = {};
        this.selectedLinesOfBusiness = [];
        this.selectedHealthPlans = [];
        this.selectedAuthSubClasses = [];
        this.allowAnyLineOfBusiness = false;
        this.allowAnyHealthPlan = false;
        this.allowAnyAuthSubClass = false;
    }

    public async submit(): Promise<boolean> {
        return this.savePortalQueue();
    }

    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadLookups(): Promise<void> {
        const sorting = new Sorting();
        sorting.entries.push({
            reference: 'description',
            direction: SortDirection.Ascending
        });

        const options: Partial<IQueryLoadOptions> = {
            all: true,
            includeInactive: false,
            sorting
        };

        try {
            this.state.beginLoad();

            const [lobResponse, hpResponse, scResponse] = await Promise.all(
                [
                    this.lookupService.loadLinesOfBusiness(options),
                    this.lookupService.loadHealthPlans(options),
                    this.authService.loadAuthSubClasses(options),
                ]);

            this.linesOfBusiness = lobResponse.results;
            this.healthPlans = hpResponse.results;
            this.authSubClasses = scResponse.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async savePortalQueue(): Promise<boolean> {
        if(!this.portalQueue) return false;

        let isSuccessful = false;
        let duplicateCodeError = false;
        try {
            this.state.beginSave();
            const response = await this.authService.savePortalQueue(this.constructSaveObject());
            isSuccessful = response.isSuccessful;
            return isSuccessful;
        } catch(error) {
            if(error.status.errors) duplicateCodeError = error.status.errors.some((err: any) => err.code === 'property.constraintViolated' && err.property === 'code');
            throw error;
        } finally {
            if(duplicateCodeError) {
                this.toaster.error().message(this.i18n.tr('ui:messages.duplicate.save_error', { what: this.i18n.tr('um:queue.code.$'), item: this.i18n.tr('um:queue.$') })).show();
            } else {
                this.toaster.standard.saved('um:portalQueue.$', isSuccessful);
            }
            this.state.endSave();
        }
    }
}
